// Word export for a drafted clinical document. Renders the same structured
// draft the server renders to PDF (documentPDF.js), so the two downloads
// match: letterhead, date, recipient, "Re:" line, title, salutation,
// sections, closing and the typed sign-off.
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  AlignmentType,
  ImageRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  VerticalAlign,
  BorderStyle,
} from "docx";
import { saveAs } from "file-saver";

const FONT = "Calibri";
const BODY = 22;     // half-points: 11pt
const SMALL = 18;    // 9pt
const HEADING = 24;  // 12pt

const run = (text, opts = {}) => new TextRun({ text, font: FONT, size: BODY, ...opts });
const para = (children, opts = {}) =>
  new Paragraph({ children: Array.isArray(children) ? children : [children], spacing: { after: 80 }, ...opts });
const blank = () => para(run(" "));

// Mirrors MONTHS and DOCUMENT_LABELS in backend clinicalDocumentService.js so
// the Word file matches the PDF line for line.
const MONTHS = {
  en: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
  af: ["Januarie", "Februarie", "Maart", "April", "Mei", "Junie", "Julie", "Augustus", "September", "Oktober", "November", "Desember"],
};
const LABELS = {
  en: { re: "Re", dob: "DOB", id: "ID" },
  af: { re: "Insake", dob: "Geboortedatum", id: "ID" },
};
function longDate(value, lang) {
  const d = value ? new Date(value) : new Date();
  if (isNaN(d.getTime())) return String(value);
  return `${d.getDate()} ${(MONTHS[lang] || MONTHS.en)[d.getMonth()]} ${d.getFullYear()}`;
}
function shortDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}

// Fetches an image and scales it to `height` px keeping its aspect ratio.
async function fetchImage(url, height) {
  if (!url) return null;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    const data = await blob.arrayBuffer();
    const dims = await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.width, height: img.height });
      img.onerror = () => resolve(null);
      img.src = URL.createObjectURL(blob);
    });
    if (!dims) return null;
    return { data, width: Math.round((height * dims.width) / dims.height), height };
  } catch (_e) {
    return null;
  }
}
const fetchLogo = (url) => fetchImage(url, 60);
const fetchSignature = (url) => fetchImage(url, 45);

const noBorder = { style: BorderStyle.NONE, size: 0, color: "FFFFFF" };

function letterhead(practice, logo) {
  const left = [];
  if (logo) {
    left.push(para(new ImageRun({ data: logo.data, transformation: { width: logo.width, height: logo.height } })));
  }
  const right = [
    para(run(practice.practiceName || "", { bold: true }), { alignment: AlignmentType.RIGHT }),
  ];
  const line = (label, value) =>
    value && right.push(para([run(label, { bold: true, size: SMALL }), run(value, { size: SMALL })], { alignment: AlignmentType.RIGHT, spacing: { after: 20 } }));
  line("Practice No: ", practice.practiceNumber);
  line("HPCSA No: ", practice.hpcsaNumber);
  line("VAT: ", practice.vatNumber);
  const addr1 = [practice.practiceAddress, practice.practiceAddress2].filter(Boolean).join(", ");
  const addr2 = [practice.practiceCity, practice.practicePostalCode].filter(Boolean).join(", ");
  if (addr1) right.push(para(run(addr1, { size: SMALL }), { alignment: AlignmentType.RIGHT, spacing: { after: 20 } }));
  if (addr2) right.push(para(run(addr2, { size: SMALL }), { alignment: AlignmentType.RIGHT, spacing: { after: 20 } }));
  line("Email: ", practice.practiceEmail);
  line("Tel: ", practice.practicePhoneNumber);

  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: { top: noBorder, bottom: { style: BorderStyle.SINGLE, size: 6, color: "505050" }, left: noBorder, right: noBorder, insideVertical: noBorder, insideHorizontal: noBorder },
    rows: [
      new TableRow({
        children: [
          new TableCell({ width: { size: 50, type: WidthType.PERCENTAGE }, verticalAlign: VerticalAlign.BOTTOM, children: left.length ? left : [blank()] }),
          new TableCell({ width: { size: 50, type: WidthType.PERCENTAGE }, verticalAlign: VerticalAlign.BOTTOM, children: right }),
        ],
      }),
    ],
  });
}

/**
 * Build and download the Word file.
 * @param {Object} p.practice  practice document (letterhead fields)
 * @param {Object} p.patient   { patientTitle, patientFirstName, patientLastName, patientDOB, patientIdNumber }
 * @param {Object} p.draft     { title, recipientName, recipientDetail, salutation, sections, closing }
 * @param {Object} p.signoff   { name, occupation, registration, practiceNumber }
 * @param {string} p.fileName  the .pdf name the server chose; swapped to .docx here
 * @param {string|Date} p.date
 * @param {string} p.language  "en" | "af"
 */
export async function downloadDocumentDocx({ practice = {}, patient = {}, draft, signoff = {}, fileName, date, language = "en" }) {
  const labels = LABELS[language] || LABELS.en;
  const [logo, signature] = await Promise.all([fetchLogo(practice.logoURL), fetchSignature(signoff.signatureURL)]);
  const children = [letterhead(practice, logo), blank()];

  children.push(para(run(longDate(date, language)), { alignment: AlignmentType.RIGHT }));
  children.push(blank());

  if (draft.recipientName) children.push(para(run(draft.recipientName, { bold: true }), { spacing: { after: 20 } }));
  if (draft.recipientDetail) children.push(para(run(draft.recipientDetail)));
  if (draft.recipientName || draft.recipientDetail) children.push(blank());

  const patientName = [patient.patientTitle, patient.patientFirstName, patient.patientLastName].filter(Boolean).join(" ");
  const reParts = [patientName];
  if (patient.patientDOB) reParts.push(`${labels.dob} ${shortDate(patient.patientDOB)}`);
  if (patient.patientIdNumber) reParts.push(`${labels.id} ${patient.patientIdNumber}`);
  children.push(para(run(`${labels.re}: ${reParts.filter(Boolean).join(", ")}`, { bold: true })));
  children.push(blank());

  if (draft.title) {
    children.push(para(run(draft.title.toUpperCase(), { bold: true })));
    children.push(blank());
  }
  if (draft.salutation) {
    children.push(para(run(draft.salutation)));
    children.push(blank());
  }

  for (const section of draft.sections || []) {
    if (section.heading) children.push(para(run(section.heading, { bold: true, size: HEADING }), { spacing: { before: 120, after: 60 } }));
    for (const p of section.paragraphs || []) children.push(para(run(p), { spacing: { after: 120 } }));
  }

  if (draft.closing) {
    children.push(blank());
    children.push(para(run(draft.closing)));
  }

  children.push(blank());
  if (signature) {
    children.push(para(new ImageRun({ data: signature.data, transformation: { width: signature.width, height: signature.height } }), { spacing: { after: 40 } }));
  } else {
    children.push(blank());
  }
  if (signoff.name) children.push(para(run(signoff.name, { bold: true }), { spacing: { after: 20 } }));
  for (const l of [signoff.occupation, signoff.registration, signoff.practiceNumber]) {
    if (l) children.push(para(run(l), { spacing: { after: 20 } }));
  }

  const doc = new Document({
    styles: { default: { document: { run: { font: FONT, size: BODY } } } },
    sections: [{ properties: { page: { margin: { top: 700, right: 700, bottom: 700, left: 700 } } }, children }],
  });

  const blob = await Packer.toBlob(doc);
  const name = (fileName || "Document.pdf").replace(/\.pdf$/i, "") + ".docx";
  saveAs(blob, name);
}
